import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Save, Loader, Upload, Trash2, Plus, RefreshCw, AlertCircle, CheckCircle, ArrowLeft, X, Edit2, Play, ChevronDown, ChevronUp, Shield, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import NotificationModal from '../components/common/modals/NotificationModal';
import EnrichmentSourcesSection from '../components/features/settings/EnrichmentSourcesSection';
import { integrationService } from '../services/IntegrationService';
import { getIntegrationConfig, IntegrationConfig, IntegrationSourceType, IntegrationField, WebCrawlAction, IntegrationSource, ActionTemplate } from '../config/integrations';
import {
  translateIntegrationConfig,
  translateSourceType,
  translateField,
  TranslatedIntegrationConfig,
  TranslatedIntegrationSourceType,
  TranslatedIntegrationField
} from '../config/integrationTranslations';



interface GenericIntegrationSetupPageProps {
  // Optional: can be passed as prop or read from URL params
  integrationId?: string;
  /** When true, uses admin shared-source endpoints instead of org-scoped endpoints */
  adminMode?: boolean;
  /** Default organisation IDs to pre-select when creating a new source (adminMode only) */
  sharedOrganizationIds?: number[];
  /** Full list of organisations available for assignment (adminMode only) */
  organizations?: { id: number; name: string }[];
  /** Show only data sources marked as DMS (config.isDms) — the Quotes (DMS / ERP) view. */
  dmsOnly?: boolean;
}

// ── Schema editor types & helpers (module-level to avoid focus-loss on re-render) ──

type SchemaProp = {
  name: string;
  type: string;
  arrayItemType: string; // items.type when type === 'array'
  description: string;
  required: boolean;
  enumValues: string;
  children?: SchemaProp[]; // fields of an object, or of an array's object items
};

const BLANK_PROP = (): SchemaProp => ({
  name: '', type: 'string', arrayItemType: 'string', description: '', required: false, enumValues: ''
});

const hasChildren = (p: SchemaProp) => p.type === 'object' || (p.type === 'array' && p.arrayItemType === 'object');

/** Editable rows → JSON Schema (recursive: object fields and array-of-object items keep their own rows). */
const buildSchema = (props: SchemaProp[]): WebCrawlAction['inputSchema'] | undefined => {
  if (props.length === 0) return undefined;
  const properties: Record<string, any> = {};
  const required: string[] = [];
  props.forEach(p => {
    if (!p.name.trim()) return;
    const prop: any = { type: p.type };
    const nested = hasChildren(p) ? buildSchema(p.children ?? []) : undefined;
    if (p.type === 'array') {
      prop.items = p.arrayItemType === 'object' && nested ? nested : { type: p.arrayItemType || 'string' };
    } else if (p.type === 'object' && nested) {
      prop.properties = nested.properties;
      if (nested.required) prop.required = nested.required;
    }
    if (p.description.trim()) prop.description = p.description.trim();
    if (p.enumValues.trim()) {
      prop.enum = p.enumValues.split(',').map(v => v.trim()).filter(Boolean);
    }
    properties[p.name.trim()] = prop;
    if (p.required) required.push(p.name.trim());
  });
  return { type: 'object', properties, required: required.length ? required : undefined };
};

/** Parse an existing JSON Schema back to editable rows (for editing) */
const schemaToRows = (schema?: WebCrawlAction['inputSchema']): SchemaProp[] => {
  if (!schema?.properties) return [];
  return Object.entries(schema.properties).map(([name, def]) => {
    const d = def as any;
    const nested = d.type === 'object' ? d : d.type === 'array' && d.items?.type === 'object' ? d.items : undefined;
    return {
      name,
      type: d.type || 'string',
      arrayItemType: d.items?.type || 'string',
      description: d.description || '',
      required: schema.required?.includes(name) ?? false,
      enumValues: d.enum ? d.enum.join(', ') : '',
      ...(nested ? { children: schemaToRows(nested) } : {}),
    };
  });
};

const SCHEMA_TYPES = ['string', 'number', 'boolean', 'array', 'object'];
const ARRAY_ITEM_TYPES = ['string', 'number', 'boolean', 'object'];

interface SchemaTableProps {
  label: string;
  hint: string;
  props: SchemaProp[];
  onAdd: () => void;
  onUpdate: (idx: number, field: keyof SchemaProp, value: any) => void;
  onDelete: (idx: number) => void;
  buildSchema: (props: SchemaProp[]) => any;
  nested?: boolean; // fields of an object / array items: no JSON preview, compact header
}

const SchemaTable: React.FC<SchemaTableProps> = ({ label, hint, props, onAdd, onUpdate, onDelete, buildSchema, nested }) => {
  const { t } = useTranslation();
  return (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div>
        <span className={nested ? 'text-xs font-medium text-blue-700' : 'text-sm font-medium text-gray-700'}>{label}</span>
        {hint && <span className="ml-2 text-xs text-gray-400">{hint}</span>}
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> {t('schemaEditor.addProperty', 'Add property')}
      </button>
    </div>
    {props.length === 0 ? (
      <div className="text-xs text-gray-400 text-center py-3 bg-white border border-dashed border-gray-200 rounded-lg">
        {t('schemaEditor.noPropertiesDefined', 'No properties defined — click "Add property" to define the schema')}
      </div>
    ) : (
      <div className="space-y-2">
        {/* Header */}
        <div className="grid grid-cols-[1fr_100px_1fr_80px_60px_28px] gap-1.5 px-2 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
          <span>{t('schemaEditor.fieldName', 'Field name')}</span>
          <span>{t('schemaEditor.type', 'Type')}</span>
          <span>{t('schemaEditor.description', 'Description')}</span>
          <span>{t('schemaEditor.enumValues', 'Enum values')}</span>
          <span>{t('schemaEditor.required', 'Required')}</span>
          <span />
        </div>
        {props.map((prop, idx) => (
          <div key={idx} className="space-y-1">
            <div className="grid grid-cols-[1fr_100px_1fr_80px_60px_28px] gap-1.5 items-center bg-white p-2 rounded-lg border border-gray-200">
              <input
                type="text"
                value={prop.name}
                onChange={e => onUpdate(idx, 'name', e.target.value)}
                placeholder="fieldName"
                className="px-2 py-1.5 border border-gray-200 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-purple-500 w-full"
              />
              <select
                value={prop.type}
                onChange={e => onUpdate(idx, 'type', e.target.value)}
                className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 w-full"
              >
                {SCHEMA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                type="text"
                value={prop.description}
                onChange={e => onUpdate(idx, 'description', e.target.value)}
                placeholder="Optional description"
                className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 w-full"
              />
              <input
                type="text"
                value={prop.enumValues}
                onChange={e => onUpdate(idx, 'enumValues', e.target.value)}
                placeholder="a, b, c"
                title="Comma-separated allowed values"
                className="px-2 py-1.5 border border-gray-200 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-purple-500 w-full"
              />
              <div className="flex justify-center">
                <input
                  type="checkbox"
                  checked={prop.required}
                  onChange={e => onUpdate(idx, 'required', e.target.checked)}
                  className="w-4 h-4 rounded accent-purple-600"
                />
              </div>
              <button
                type="button"
                onClick={() => onDelete(idx)}
                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Array items sub-type */}
            {prop.type === 'array' && (
              <div className="ml-6 flex items-center gap-2 py-1.5 px-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-[11px] text-blue-600 font-medium whitespace-nowrap">{t('schemaEditor.itemsType', 'items type:')}</span>
                <select
                  value={prop.arrayItemType}
                  onChange={e => onUpdate(idx, 'arrayItemType', e.target.value)}
                  className="px-2 py-1 border border-blue-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {ARRAY_ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-[11px] text-blue-400">{t('schemaEditor.itemsTypeHint', '→ items of this type inside the array')}</span>
              </div>
            )}
            {/* Fields of an object, or of the array's object items */}
            {hasChildren(prop) && (
              <div className="ml-6 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg">
                <SchemaTable
                  nested
                  label={prop.type === 'array' ? t('schemaEditor.itemFields', 'Fields of each item') : t('schemaEditor.objectFields', 'Fields')}
                  hint=""
                  props={prop.children ?? []}
                  onAdd={() => onUpdate(idx, 'children', [...(prop.children ?? []), BLANK_PROP()])}
                  onUpdate={(cIdx, field, value) => onUpdate(idx, 'children', (prop.children ?? []).map((c, i) => i === cIdx ? { ...c, [field]: value } : c))}
                  onDelete={(cIdx) => onUpdate(idx, 'children', (prop.children ?? []).filter((_, i) => i !== cIdx))}
                  buildSchema={buildSchema}
                />
              </div>
            )}
          </div>
        ))}
        {/* JSON preview */}
        {!nested && (
        <details className="mt-1">
          <summary className="text-[11px] text-gray-400 cursor-pointer select-none hover:text-gray-600">{t('schemaEditor.previewJsonSchema', 'Preview JSON Schema')}</summary>
          <pre className="mt-1 p-2 bg-gray-900 text-green-400 rounded text-[11px] font-mono overflow-auto max-h-40 whitespace-pre-wrap">
            {JSON.stringify(buildSchema(props), null, 2)}
          </pre>
        </details>
        )}
      </div>
    )}
  </div>
  );
};

// ── CsvActionBuilder ─────────────────────────────────────────────────────────
// Renders when a source type has `actionTemplates`.
// Allows the user to create one or more named actions, each with column mappings.

interface CsvActionBuilderProps {
  templates: ActionTemplate[];
  csvHeaders: string[];
  actions: WebCrawlAction[];
  onChange: (actions: WebCrawlAction[]) => void;
}

const CsvActionBuilder: React.FC<CsvActionBuilderProps> = ({ templates, csvHeaders, actions, onChange }) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ActionTemplate | null>(null);
  const [actionName, setActionName] = useState('');
  const [actionDescription, setActionDescription] = useState('');
  const [inputMapping, setInputMapping] = useState<Record<string, string>>({});
  const [outputMapping, setOutputMapping] = useState<Record<string, string>>({});
  // For editable input slots
  const [editableInputSlots, setEditableInputSlots] = useState<{ id: string; label: string }[]>([]);
  const [newInputSlotId, setNewInputSlotId] = useState('');
  // For editable output slots (general_lookup)
  const [editableOutputSlots, setEditableOutputSlots] = useState<{ id: string; label: string }[]>([]);
  const [newSlotId, setNewSlotId] = useState('');

  const resetForm = () => {
    setEditingId(null);
    setSelectedTemplate(null);
    setActionName('');
    setActionDescription('');
    setInputMapping({});
    setOutputMapping({});
    setEditableInputSlots([]);
    setNewInputSlotId('');
    setEditableOutputSlots([]);
    setNewSlotId('');
    setShowForm(false);
  };

  const handleSelectTemplate = (tpl: ActionTemplate) => {
    setSelectedTemplate(tpl);
    setActionName(tpl.name);
    setActionDescription(tpl.description);
    setInputMapping({});
    setOutputMapping({});
    setEditableInputSlots(tpl.inputSlotsEditable ? tpl.inputSlots.map(s => ({ id: s.id, label: s.label })) : []);
    setEditableOutputSlots(tpl.outputSlotsEditable ? [] : tpl.outputSlots.map(s => ({ id: s.id, label: s.label })));
  };

  const handleEdit = (action: WebCrawlAction) => {
    const tpl = templates.find(t => t.id === action.template_id) ?? templates[0];
    setEditingId(action.id);
    setSelectedTemplate(tpl);
    setActionName(action.name);
    setActionDescription(action.description || '');
    setInputMapping(action.column_mapping?.input ?? {});
    setOutputMapping(action.column_mapping?.output ?? {});
    if (tpl.inputSlotsEditable) {
      setEditableInputSlots(Object.keys(action.column_mapping?.input ?? {}).map(id => ({ id, label: id })));
    } else {
      setEditableInputSlots([]);
    }
    if (tpl.outputSlotsEditable) {
      setEditableOutputSlots(Object.keys(action.column_mapping?.output ?? {}).map(id => ({ id, label: id })));
    } else {
      setEditableOutputSlots(tpl.outputSlots.map(s => ({ id: s.id, label: s.label })));
    }
    setShowForm(true);
  };

  const handleSave = () => {
    if (!selectedTemplate || !actionName.trim()) return;

    // Build schemas from mapping
    const inputProps: Record<string, any> = {};
    const outputProps: Record<string, any> = {};

    const inputSlotsToUse = selectedTemplate.inputSlotsEditable ? editableInputSlots : selectedTemplate.inputSlots;
    inputSlotsToUse.forEach(slot => {
      if (inputMapping[slot.id]) {
        inputProps[slot.id] = { type: 'string', description: (slot as any).description || slot.label };
      }
    });

    const outputSlotsToUse = selectedTemplate.outputSlotsEditable ? editableOutputSlots : selectedTemplate.outputSlots;
    outputSlotsToUse.forEach(slot => {
      if (outputMapping[slot.id]) {
        outputProps[slot.id] = { type: 'string', description: slot.label };
      }
    });

    const action: WebCrawlAction = {
      id: editingId ?? `csv-action-${Date.now()}`,
      name: actionName.trim(),
      description: actionDescription,
      template_id: selectedTemplate.id,
      inputParameters: inputSlotsToUse
        .filter(s => inputMapping[s.id])
        .map(s => ({ name: s.id, type: 'string' as const, required: !!(s as any).required, description: (s as any).description || s.label })),
      outputFormat: { type: 'json' },
      instructions: '',
      column_mapping: {
        input: Object.fromEntries(
          Object.entries(inputMapping).filter(([, v]) => v)
        ),
        output: Object.fromEntries(
          Object.entries(outputMapping).filter(([, v]) => v)
        ),
      },
      inputSchema: {
        type: 'object',
        properties: inputProps,
        required: inputSlotsToUse.filter(s => (s as any).required && inputMapping[s.id]).map(s => s.id),
      },
      // Output is { results: [...rows...], rows_count, found }
      // where each row has the mapped slot names as keys.
      outputSchema: {
        type: 'object',
        properties: {
          found: { type: 'boolean', description: 'Whether any rows were found' },
          rows_count: { type: 'number', description: 'Number of matching rows' },
          results: {
            type: 'array',
            description: 'Matching rows',
            items: {
              type: 'object',
              properties: outputProps,
            },
          } as any,
        },
      },
    };

    if (editingId) {
      onChange(actions.map(a => a.id === editingId ? action : a));
    } else {
      onChange([...actions, action]);
    }
    resetForm();
  };

  const handleDelete = (id: string) => onChange(actions.filter(a => a.id !== id));

  const inputSlotsForForm = selectedTemplate?.inputSlotsEditable
    ? editableInputSlots
    : (selectedTemplate?.inputSlots ?? []);

  const outputSlotsForForm = selectedTemplate?.outputSlotsEditable
    ? editableOutputSlots
    : (selectedTemplate?.outputSlots ?? []);

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium text-gray-900">{t('actionBuilder.actionsTitle', 'Actions')}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('actionBuilder.actionsSubtitle', 'Define what this data source does — each action will be available as a workflow step.')}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('actionBuilder.addAction', 'Add Action')}
          </button>
        )}
      </div>

      {/* Action form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200 space-y-4">
          {/* Step 1: pick template */}
          {!selectedTemplate ? (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">{t('actionBuilder.whatShouldActionDo', 'What should this action do?')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {templates.map(tpl => (
                  <button
                    key={tpl.id}
                    onClick={() => handleSelectTemplate(tpl)}
                    className="text-left p-3 border-2 border-gray-200 hover:border-brand-400 rounded-lg transition-colors bg-white"
                  >
                    <p className="text-sm font-medium text-gray-900">{tpl.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{tpl.description}</p>
                  </button>
                ))}
              </div>
              <button onClick={resetForm} className="mt-3 text-sm text-gray-500 hover:text-gray-700">{t('common.cancel', 'Cancel')}</button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('actionBuilder.actionNameRequired', 'Action Name')} <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={actionName}
                  onChange={e => setActionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {/* Input column mapping */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">{t('actionBuilder.inputLookupKeys', 'Input — Lookup Key(s)')}</p>
                  {selectedTemplate.inputSlotsEditable && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newInputSlotId}
                        onChange={e => setNewInputSlotId(e.target.value)}
                        placeholder="field name"
                        className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 w-32"
                      />
                      <button
                        onClick={() => {
                          if (newInputSlotId.trim()) {
                            setEditableInputSlots(prev => [...prev, { id: newInputSlotId.trim(), label: newInputSlotId.trim() }]);
                            setNewInputSlotId('');
                          }
                        }}
                        className="text-xs px-2 py-1 bg-brand-500 text-white rounded-lg hover:bg-brand-600"
                      >
                        {t('actionBuilder.addField', '+ Add')}
                      </button>
                    </div>
                  )}
                </div>
                {inputSlotsForForm.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-2">{t('actionBuilder.addLookupKeyHint', 'Add lookup key fields above ↑')}</p>
                )}
                <div className="space-y-2">
                  {inputSlotsForForm.map(slot => (
                    <div key={slot.id} className="flex items-center gap-3">
                      <div className="w-44 flex-shrink-0 flex items-center gap-1">
                        <span className="text-sm text-gray-600">{slot.label || slot.id}</span>
                        {(slot as any).required && <span className="text-red-500 ml-0.5">*</span>}
                        {selectedTemplate.inputSlotsEditable && (
                          <button
                            onClick={() => {
                              setEditableInputSlots(prev => prev.filter(s => s.id !== slot.id));
                              setInputMapping(prev => { const n = { ...prev }; delete n[slot.id]; return n; });
                            }}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <select
                        value={inputMapping[slot.id] || ''}
                        onChange={e => setInputMapping(prev => ({ ...prev, [slot.id]: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none bg-white"
                      >
                        <option value="">{t('actionBuilder.selectColumn', '— Select column —')}</option>
                        {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Output column mapping */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">{t('actionBuilder.outputReturnedFields', 'Output — Returned Fields')}</p>
                  {selectedTemplate.outputSlotsEditable && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newSlotId}
                        onChange={e => setNewSlotId(e.target.value)}
                        placeholder="field name"
                        className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 w-32"
                      />
                      <button
                        onClick={() => {
                          if (newSlotId.trim()) {
                            setEditableOutputSlots(prev => [...prev, { id: newSlotId.trim(), label: newSlotId.trim() }]);
                            setNewSlotId('');
                          }
                        }}
                        className="text-xs px-2 py-1 bg-brand-500 text-white rounded-lg hover:bg-brand-600"
                      >
                        {t('actionBuilder.addField', '+ Add')}
                      </button>
                    </div>
                  )}
                </div>
                {outputSlotsForForm.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-2">{t('actionBuilder.addOutputFieldHint', 'Add output fields above ↑')}</p>
                )}
                <div className="space-y-2">
                  {outputSlotsForForm.map(slot => (
                    <div key={slot.id} className="flex items-center gap-3">
                      <div className="w-44 flex-shrink-0 flex items-center gap-1">
                        <span className="text-sm text-gray-600">{slot.label || slot.id}</span>
                        {selectedTemplate.outputSlotsEditable && (
                          <button
                            onClick={() => {
                              setEditableOutputSlots(prev => prev.filter(s => s.id !== slot.id));
                              setOutputMapping(prev => { const n = { ...prev }; delete n[slot.id]; return n; });
                            }}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <select
                        value={outputMapping[slot.id] || ''}
                        onChange={e => setOutputMapping(prev => ({ ...prev, [slot.id]: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none bg-white"
                      >
                        <option value="">{t('actionBuilder.optionalColumn', '— Optional —')}</option>
                        {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form footer */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel', 'Cancel')}</button>
                <button
                  onClick={handleSave}
                  disabled={!actionName.trim() || inputSlotsForForm.filter(s => (s as any).required).some(s => !inputMapping[s.id])}
                  className="px-4 py-2 text-sm bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
                >
                  {editingId ? t('actionBuilder.updateAction', 'Update Action') : t('actionBuilder.saveAction', 'Save Action')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions list */}
      {actions.length > 0 && (
        <div className="space-y-2">
          {actions.map(action => {
            const inputCols = Object.values(action.column_mapping?.input ?? {}).filter(Boolean);
            const outputCols = Object.values(action.column_mapping?.output ?? {}).filter(Boolean);
            return (
              <div key={action.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{action.name}</p>
                    {action.description && <p className="text-xs text-gray-500 mt-0.5">{action.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {inputCols.length > 0 && (
                        <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">
                          IN: {inputCols.join(', ')}
                        </span>
                      )}
                      {outputCols.length > 0 && (
                        <span className="text-[10px] bg-green-50 text-green-700 border border-green-200 rounded px-2 py-0.5">
                          OUT: {outputCols.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => handleEdit(action)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(action.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {actions.length === 0 && !showForm && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <p className="text-sm text-gray-500">{t('actionBuilder.noActionsDefined', 'No actions defined yet.')}</p>
          <p className="text-xs text-gray-400 mt-0.5">{t('actionBuilder.addActionHint', 'Add at least one action so this data source can be used in workflows.')}</p>
        </div>
      )}
    </div>
  );
};

// ── End CsvActionBuilder ─────────────────────────────────────────────────────

const GenericIntegrationSetupPage: React.FC<GenericIntegrationSetupPageProps> = ({ integrationId: propIntegrationId, adminMode = false, sharedOrganizationIds = [], organizations = [], dmsOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { integrationId: paramIntegrationId } = useParams<{ integrationId: string }>();

  const integrationId = propIntegrationId || paramIntegrationId;
  const integrationConfigRaw = integrationId ? getIntegrationConfig(integrationId) : null;

  // Create a wrapper for the t function that matches our helper's signature
  const translate = (key: string, defaultValue?: string): string => {
    return t(key, { defaultValue: defaultValue || key });
  };

  // Translate the integration config
  const integrationConfig = integrationConfigRaw ? translateIntegrationConfig(integrationConfigRaw, translate) : null;

  // State
  const [sources, setSources] = useState<IntegrationSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<IntegrationSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Direct-to-GCS upload progress (0-100) while a large file is uploading.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showAddSource, setShowAddSource] = useState(false);
  // Supplier page sub-tabs: the integration sources vs the reference-document
  // library (manuals/pautas). The sources pane stays mounted (hidden) so its
  // state survives tab switches.
  const [supplierTab, setSupplierTab] = useState<'sources' | 'refdocs'>('sources');
  const [editingSource, setEditingSource] = useState<IntegrationSource | null>(null);

  // Org scoping state
  const [isOrgScoped, setIsOrgScoped] = useState(false);
  const [isOrgAdmin, setIsOrgAdmin] = useState(true);

  // Form state
  const [sourceName, setSourceName] = useState('');
  const [sourceType, setSourceType] = useState<string>('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  // "Test connection" for MCP sources: lists the server's tools with the unsaved form config.
  const [mcpTest, setMcpTest] = useState<{ status: 'idle' | 'running' | 'ok' | 'fail'; tools?: { name: string; description: string }[]; error?: string }>({ status: 'idle' });
  const testMcpConnection = async () => {
    setMcpTest({ status: 'running' });
    try {
      const res = await integrationService.testMcpConnection(formData);
      setMcpTest(res.success ? { status: 'ok', tools: res.tools } : { status: 'fail', error: res.error });
    } catch (e: any) {
      setMcpTest({ status: 'fail', error: e?.response?.data?.detail || e?.message || String(e) });
    }
  };
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Action builder state (for webcrawl)
  const [crawlActions, setCrawlActions] = useState<WebCrawlAction[]>([]);
  const [showActionForm, setShowActionForm] = useState(false);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [actionName, setActionName] = useState('');
  const [actionDescription, setActionDescription] = useState('');
  const [actionInstructions, setActionInstructions] = useState('');
  const [actionTool, setActionTool] = useState('');  // MCP sources: the server tool this action calls
  useEffect(() => {
    if (showActionForm && sourceType === 'mcp' && mcpTest.status === 'idle' && formData.mcpUrl) testMcpConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActionForm, sourceType]);
  // Structured schema editors (replaces legacy actionInputs + actionOutputSchema)
  const [inputSchemaProps, setInputSchemaProps] = useState<SchemaProp[]>([]);
  const [outputSchemaProps, setOutputSchemaProps] = useState<SchemaProp[]>([]);

  // CSV column-mapping modal state
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [showCsvMappingModal, setShowCsvMappingModal] = useState(false);
  const [pendingCsvFile, setPendingCsvFile] = useState<File | null>(null);
  const [csvMappingState, setCsvMappingState] = useState<Record<string, string>>({});
  const [parsingFile, setParsingFile] = useState(false);

  // Admin: org assignment for the source being created/edited
  const [localOrgIds, setLocalOrgIds] = useState<number[]>(sharedOrganizationIds);

  // Action testing state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testingAction, setTestingAction] = useState<WebCrawlAction | null>(null);
  const [testInputValues, setTestInputValues] = useState<Record<string, any>>({});
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    output: any;
    executionTime: number;
    error?: string;
  } | null>(null);

  // Running tests tracker (for parallel execution)
  const [runningTests, setRunningTests] = useState<Map<string, {
    action: WebCrawlAction;
    source: IntegrationSource;
    status: 'running' | 'success' | 'failed';
    result?: any;
    error?: string;
    startTime: number;
  }>>(new Map());

  // Notification state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    if (integrationConfig) {
      loadSources();
    }
  }, [integrationId]);

  const loadSources = async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      if (adminMode) {
        const response = await integrationService.getSharedSources(integrationId);
        setSources((response.data || []).map((s: any) => ({ ...s, type: s.source_type ?? s.type })));
        setIsOrgScoped(false);
        setIsOrgAdmin(true);
      } else {
        const response = await integrationService.getSources(integrationId);
        setSources((response.data.sources || []).filter((s: IntegrationSource) => !dmsOnly || s.config?.isDms));
        setIsOrgScoped(response.data.isOrgScoped ?? false);
        setIsOrgAdmin(response.data.isOrgAdmin ?? true);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-poll sources while any integration source is building embeddings in background
  useEffect(() => {
    const hasProcessingEmbeddings = sources.some(s => {
      const cfg = (s.config || {}) as Record<string, any>;
      return cfg.embedding_status === 'processing';
    });

    if (hasProcessingEmbeddings) {
      const timer = setInterval(() => {
        loadSources();
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [sources, adminMode, integrationId]);

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ isOpen: true, title, message, type });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const resetForm = () => {
    setSourceName('');
    setSourceType('');
    setFormData({});
    setSelectedFile(null);
    setCrawlActions([]);
    setShowActionForm(false);
    setEditingActionId(null);
    setActionName('');
    setActionDescription('');
    setActionInstructions('');
    setInputSchemaProps([]);
    setOutputSchemaProps([]);
    setEditingSource(null);
  };

  const handleAddSource = () => {
    if (isOrgScoped && !isOrgAdmin) {
      showNotification(t('sourceManagement.permissionDenied', 'Permission Denied'), t('sourceManagement.adminOnlyMessage', 'Only organization admins can manage integration sources'), 'error');
      return;
    }
    setShowAddSource(true);
    setSelectedSource(null);
    setEditingSource(null);
    setLocalOrgIds(sharedOrganizationIds);
    resetForm();
    // Auto-select the first source type (Cata Magic Agent)
    const firstSourceType = integrationConfig?.sourceTypes[0]?.id;
    if (firstSourceType) {
      setSourceType(firstSourceType);
    }
  };

  const handleEditSource = (source: IntegrationSource) => {
    if (isOrgScoped && !isOrgAdmin) {
      showNotification(t('sourceManagement.permissionDenied', 'Permission Denied'), t('sourceManagement.adminOnlyMessage', 'Only organization admins can manage integration sources'), 'error');
      return;
    }
    setEditingSource(source);
    setShowAddSource(true);
    setSourceName(source.name);
    setSourceType(source.type);
    setFormData(source.config || {});
    setCrawlActions(source.actions || []);
    // In admin mode, restore the org IDs assigned to this source
    if (adminMode) {
      setLocalOrgIds((source as any).shared_organization_ids || sharedOrganizationIds);
    }
    // Restore csvHeaders from the stored column-mapping values so CsvActionBuilder
    // dropdowns are populated even when no new file has been uploaded.
    if (source.config) {
      const colValues = Object.entries(source.config)
        .filter(([k, v]) => k.endsWith('_column') && typeof v === 'string' && v)
        .map(([, v]) => v as string);
      if (colValues.length > 0) {
        setCsvHeaders(colValues);
      } else if (source.actions?.length) {
        // For supplier-type sources (no _column config fields), reconstruct headers
        // from the column mappings stored inside existing actions.
        const headersFromActions = new Set<string>();
        source.actions.forEach(action => {
          const inputCols = Object.values(action.column_mapping?.input ?? {});
          const outputCols = Object.values(action.column_mapping?.output ?? {});
          [...inputCols, ...outputCols].forEach(col => { if (col) headersFromActions.add(col as string); });
          // Also pull from inputParameters names as fallback
          action.inputParameters?.forEach(p => headersFromActions.add(p.name));
        });
        if (headersFromActions.size > 0) {
          setCsvHeaders(Array.from(headersFromActions));
        }
      }
    }
    // Reset action form
    setShowActionForm(false);
    setEditingActionId(null);
    setInputSchemaProps([]);
    setOutputSchemaProps([]);
  };

  const handleCancelAdd = () => {
    setShowAddSource(false);
    resetForm();
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // ── Embedding columns (vector search) ────────────────────────────────────
  // Stored as a comma-separated list in formData.embedding_columns; at dataset
  // refresh the backend CONCATs these columns into AI.EMBED when materializing
  // the native BigQuery table, adding an `embeddings` column for vector search.
  const embeddingColumns = String(formData.embedding_columns || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const toggleEmbeddingColumn = (col: string) => {
    const next = embeddingColumns.includes(col)
      ? embeddingColumns.filter(c => c !== col)
      : [...embeddingColumns, col];
    handleFieldChange('embedding_columns', next.join(','));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: TranslatedIntegrationField) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (field.validation?.fileTypes) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!field.validation.fileTypes.includes(ext)) {
        showNotification('Invalid File', `Please upload a file with one of these extensions: ${field.validation.fileTypes.join(', ')}`, 'error');
        return;
      }
    }

    if (field.validation?.maxSize && file.size > field.validation.maxSize * 1024 * 1024) {
      showNotification('File Too Large', `Maximum file size is ${field.validation.maxSize}MB`, 'error');
      return;
    }

    // Check if this integration source type has column-mapping fields OR actionTemplates
    const selectedSourceTypeLocal = integrationConfigRaw?.sourceTypes.find(st => st.id === sourceType);
    const hasColumnMappings = selectedSourceTypeLocal?.fields.some(f => f.id.endsWith('_column'));
    const hasActionTemplates = !!(selectedSourceTypeLocal?.actionTemplates?.length);

    if ((hasColumnMappings || hasActionTemplates) && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      const reader = new FileReader();
      setParsingFile(true);
      reader.onload = (evt) => {
        try {
          let headers: string[] = [];
          if (isExcel) {
            const data = new Uint8Array(evt.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<string[]>(firstSheet, { header: 1 });
            headers = ((rows[0] as any[]) || []).map(h => String(h).trim()).filter(Boolean);
          } else {
            const text = evt.target?.result as string;
            const firstLine = text.split(/\r?\n/)[0] || '';
            headers = firstLine.split(',').map(h => h.replace(/^"|"$/g, '').trim()).filter(Boolean);
          }
          setCsvHeaders(headers);
          setPendingCsvFile(file);
          // Pre-populate mapping from existing formData
          const initialMapping: Record<string, string> = {};
          selectedSourceTypeLocal?.fields.filter(f => f.id.endsWith('_column')).forEach(f => {
            initialMapping[f.id] = formData[f.id] || '';
          });
          setCsvMappingState(initialMapping);

          if (hasActionTemplates && !hasColumnMappings) {
            // For action-template sources just store the file + headers; no legacy modal needed
            setSelectedFile(file);
            handleFieldChange(field.id, file.name);
          } else {
            setShowCsvMappingModal(true);
          }
        } finally {
          setParsingFile(false);
        }
      };
      reader.onerror = () => setParsingFile(false);
      if (isExcel) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    } else {
      setSelectedFile(file);
      handleFieldChange(field.id, file.name);
    }
  };

  const handleCsvMappingConfirm = () => {
    if (!pendingCsvFile) return;
    setSelectedFile(pendingCsvFile);
    handleFieldChange('file', pendingCsvFile.name);
    // Merge column mappings into formData
    setFormData(prev => ({ ...prev, ...csvMappingState }));
    setShowCsvMappingModal(false);
    setPendingCsvFile(null);
  };

  const handleSaveSource = async () => {
    if (!sourceName.trim()) {
      showNotification('Validation Error', 'Please enter a source name', 'error');
      return;
    }

    if (!sourceType) {
      showNotification('Validation Error', 'Please select a source type', 'error');
      return;
    }

    const selectedSourceTypeLocal = integrationConfig?.sourceTypes.find(st => st.id === sourceType);
    if (!selectedSourceTypeLocal) return;

    // Validate required fields
    for (const field of selectedSourceTypeLocal.fields) {
      if (field.required && !formData[field.id] && field.type !== 'file') {
        showNotification('Validation Error', `${field.label} is required`, 'error');
        return;
      }
      // A required file must actually be selected when creating a new source —
      // otherwise the source is created without a dataset and can never execute.
      if (field.required && field.type === 'file' && !selectedFile && !editingSource) {
        showNotification('Validation Error', `${field.label} is required`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      if (!integrationId) return;

      if (selectedFile && editingSource && adminMode) {
        // Editing an existing shared source with a new file: replace the file
        // in place (keeps the source id stable for workflows), then apply the
        // form's name/config/org changes. Direct-to-GCS via signed URL — no
        // request-size limit.
        await integrationService.replaceSharedSourceFile(editingSource.id, integrationId!, selectedFile, setUploadProgress);
        setUploadProgress(null);
        const editActions = selectedSourceTypeLocal.supportsActions
          ? crawlActions
          : selectedSourceTypeLocal.defaultActions?.length
            ? selectedSourceTypeLocal.defaultActions
            : undefined;
        const { file: _file, ...restFormData } = formData;
        await integrationService.updateSharedSource(editingSource.id, {
          name: sourceName,
          config: { ...restFormData },
          actions: editActions ? editActions : undefined,
          shared_organization_ids: localOrgIds,
        });
        showNotification('Success', `${integrationConfig?.name} source updated successfully`, 'success');
      } else if (selectedFile) {
        // Handle file upload — direct-to-GCS via signed URL so large files
        // bypass Cloud Run's 32 MiB request-body limit.
        const columns: Record<string, string> = {};
        Object.entries(formData).forEach(([k, v]) => {
          if (k === 'file') return; // the File object is uploaded separately
          if (v !== undefined && v !== null && v !== '') {
            columns[k] = String(v);
          }
        });
        // User-defined actions for action-template sources, else defaultActions
        const uploadActions = selectedSourceTypeLocal.supportsActions && crawlActions.length
          ? crawlActions : undefined;
        const uploadDefaultActions = !uploadActions && selectedSourceTypeLocal.defaultActions?.length
          ? selectedSourceTypeLocal.defaultActions : undefined;

        if (adminMode) {
          const response = await integrationService.uploadSharedSourceFileViaSignedUrl(integrationId!, selectedFile, {
            name: sourceName,
            type: sourceType,
            source_type: sourceType,
            columns,
            actions: uploadActions,
            default_actions: uploadDefaultActions,
            shared_organization_ids: localOrgIds,
            onProgress: setUploadProgress,
          });
          console.log('Admin shared file uploaded successfully:', response);
        } else {
          const response = await integrationService.uploadSourceFileViaSignedUrl(integrationId!, selectedFile, {
            name: sourceName,
            type: sourceType,
            columns,
            actions: uploadActions,
            default_actions: uploadDefaultActions,
            onProgress: setUploadProgress,
          });
          console.log('File uploaded successfully:', response);
        }
        setUploadProgress(null);

        showNotification('Success', `${integrationConfig?.name} source created successfully`, 'success');
      } else if (editingSource) {
        // Update existing source
        const actions = selectedSourceTypeLocal.supportsActions
          ? crawlActions
          : selectedSourceTypeLocal.defaultActions?.length
            ? selectedSourceTypeLocal.defaultActions
            : undefined;

        const sourceData = {
          name: sourceName,
          type: sourceType,
          config: {
            ...formData,
            actions
          }
        };

        if (adminMode) {
          await integrationService.updateSharedSource(editingSource.id, {
            name: sourceName,
            config: { ...formData },
            actions: actions ? actions : undefined,
            shared_organization_ids: localOrgIds,
          });
        } else {
          const response = await integrationService.updateSource(integrationId, editingSource.id, sourceData);
          console.log('Source updated successfully:', response);
        }
        showNotification('Success', `${integrationConfig?.name} source updated successfully`, 'success');
      } else {
        // Create source with configuration
        const actions = selectedSourceTypeLocal.supportsActions
          ? crawlActions
          : selectedSourceTypeLocal.defaultActions?.length
            ? selectedSourceTypeLocal.defaultActions
            : undefined;

        const sourceData = {
          name: sourceName,
          type: sourceType,
          config: {
            ...formData,
            actions
          }
        };

        if (adminMode) {
          await integrationService.createSharedSource({
            name: sourceName,
            integration_type: integrationId!,
            source_type: sourceType,
            config: { ...formData },
            actions: actions ? actions : undefined,
            shared_organization_ids: localOrgIds,
          });
        } else {
          const response = await integrationService.createSource(integrationId, sourceData);
          console.log('Source created successfully:', response);
        }
        showNotification('Success', `${integrationConfig?.name} source created successfully`, 'success');
      }

      setShowAddSource(false);
      resetForm();
      await loadSources();
    } catch (error: any) {
      console.error('Failed to save source:', error);
      showNotification('Error', error.response?.data?.detail || error.response?.data?.message || 'Failed to save source', 'error');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  // DMS flag lives in config.isDms; the update endpoint merges config, so
  // sending just the flag leaves the rest of the source untouched.
  const handleToggleDms = async (source: IntegrationSource) => {
    if (!integrationId) return;
    const isDms = !source.config?.isDms;
    try {
      if (adminMode) {
        await integrationService.updateSharedSource(source.id, { config: { isDms } });
      } else {
        await integrationService.updateSource(integrationId, source.id, { name: source.name, type: source.type, config: { isDms } });
      }
      setSources(prev => prev
        .map(s => (s.id === source.id ? { ...s, config: { ...(s.config || {}), isDms } } : s))
        .filter(s => !dmsOnly || s.config?.isDms));
    } catch (error: any) {
      showNotification(t('common.error', 'Error'), error?.response?.data?.detail || t('sourceManagement.dmsUpdateFailed', 'Failed to update source'), 'error');
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (isOrgScoped && !isOrgAdmin) {
      showNotification(t('sourceManagement.permissionDenied', 'Permission Denied'), t('sourceManagement.adminOnlyMessage', 'Only organization admins can manage integration sources'), 'error');
      return;
    }
    if (!window.confirm(t('sourceManagement.deleteConfirm', 'Are you sure you want to delete this source?'))) return;

    try {
      if (integrationId) {
        if (adminMode) {
          await integrationService.deleteSharedSource(sourceId);
        } else {
          await integrationService.deleteSource(integrationId, sourceId);
        }
        setSources(prev => prev.filter(s => s.id !== sourceId));
        showNotification('Success', 'Source deleted successfully', 'success');
      }
    } catch (error: any) {
      console.error('Failed to delete source:', error);
      showNotification('Error', error.response?.data?.detail || error.response?.data?.message || 'Failed to delete source', 'error');
    }
  };

  // ── helpers ──────────────────────────────────────────────────────────────
  /** Build a JSON Schema object from the structured property rows */
  const resetActionForm = () => {
    setEditingActionId(null);
    setActionName('');
    setActionDescription('');
    setActionInstructions('');
    setActionTool('');
    setInputSchemaProps([]);
    setOutputSchemaProps([]);
    setShowActionForm(false);
  };

  // Schema prop helpers
  const blankProp = (): SchemaProp => BLANK_PROP();
  const updateSchemaProp = (setter: React.Dispatch<React.SetStateAction<SchemaProp[]>>, idx: number, field: keyof SchemaProp, value: any) => {
    setter(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const deleteSchemaProp = (setter: React.Dispatch<React.SetStateAction<SchemaProp[]>>, idx: number) => {
    setter(prev => prev.filter((_, i) => i !== idx));
  };
  const handleAddAction = () => {
    if (!actionName.trim()) {
      showNotification(t('common.error'), t('actionBuilder.validationError.enterActionName'), 'error');
      return;
    }
    if (sourceType === 'mcp' && !actionTool) {
      showNotification(t('common.error'), t('actionBuilder.mcpToolPick', '— pick a tool —'), 'error');
      return;
    }

    const builtInputSchema = buildSchema(inputSchemaProps);
    const builtOutputSchema = buildSchema(outputSchemaProps);

    // Keep legacy inputParameters in sync for backwards compat
    const legacyInputParams = (builtInputSchema?.properties
      ? Object.entries(builtInputSchema.properties).map(([name, def]) => ({
          name,
          type: (def.type === 'number' ? 'number' : 'string') as 'string' | 'number',
          required: builtInputSchema.required?.includes(name) ?? false,
          description: def.description || ''
        }))
      : []);

    const newAction: WebCrawlAction = {
      id: editingActionId || `action-${Date.now()}`,
      name: actionName,
      description: actionDescription,
      inputParameters: legacyInputParams,
      outputFormat: {
        type: 'json',
        schema: builtOutputSchema ? JSON.stringify(builtOutputSchema) : undefined
      },
      instructions: actionInstructions,
      inputSchema: builtInputSchema,
      outputSchema: builtOutputSchema,
      ...(sourceType === 'mcp' && actionTool ? { column_mapping: { input: {}, output: {}, tool: actionTool } } : {})
    };

    if (editingActionId) {
      setCrawlActions(prev => prev.map(a => a.id === editingActionId ? newAction : a));
    } else {
      setCrawlActions(prev => [...prev, newAction]);
    }
    resetActionForm();
  };

  const handleEditAction = (action: WebCrawlAction) => {
    setEditingActionId(action.id);
    setActionName(action.name);
    setActionDescription(action.description || '');
    setActionInstructions(action.instructions || '');
    setActionTool(action.column_mapping?.tool || '');
    setInputSchemaProps(schemaToRows(action.inputSchema));
    setOutputSchemaProps(schemaToRows(action.outputSchema));
    setShowActionForm(true);
  };

  const handleDeleteAction = (actionId: string) => {
    setCrawlActions(prev => prev.filter(a => a.id !== actionId));
  };

  const handleTestAction = (action: WebCrawlAction, source?: IntegrationSource) => {
    setTestingAction(action);
    setTestInputValues({});
    setTestResult(null);
    setSelectedSource(source || null);
    setShowTestModal(true);
  };

  const handleRunTest = async () => {
    if (!testingAction || !integrationId) return;

    // Check if we have a source
    if (!selectedSource) {
      showNotification('Validation Error', 'Please save the source before testing actions', 'error');
      return;
    }

    // Validate required inputs
    for (const param of testingAction.inputParameters) {
      if (param.required && !testInputValues[param.name]) {
        showNotification('Validation Error', `${param.name} is required`, 'error');
        return;
      }
    }

    // Generate unique test ID for tracking
    const testId = `${selectedSource.id}-${testingAction.id}-${Date.now()}`;
    // Capture locally before clearing state
    const actionSnapshot = testingAction;
    const sourceSnapshot = selectedSource;

    // Close the input modal immediately
    setShowTestModal(false);
    setTestingAction(null);
    setTestResult(null);

    // Add to running tests with pending status
    setRunningTests(prev => {
      const newMap = new Map(prev);
      newMap.set(testId, {
        action: actionSnapshot,
        source: sourceSnapshot,
        status: 'running',
        startTime: Date.now()
      });
      return newMap;
    });

    // Run test in background
    try {
      const response = await integrationService.testAction(integrationId, {
        sourceId: sourceSnapshot.id,
        actionId: actionSnapshot.id,
        testInputs: testInputValues
      });

      const resultData = response.data;

      // Update to success
      setRunningTests(prev => {
        const newMap = new Map(prev);
        newMap.set(testId, {
          action: actionSnapshot,
          source: sourceSnapshot,
          status: 'success',
          result: resultData,
          startTime: Date.now()
        });
        return newMap;
      });

      // Show result modal automatically
      setTestingAction(actionSnapshot);
      setTestResult(resultData);
      setShowTestModal(true);

      showNotification('Test Complete', `${actionSnapshot.name} completed successfully`, 'success');
    } catch (error: any) {
      console.error('Test failed:', error);

      // Handle validation errors or other API errors
      let errorMessage = 'Test failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map((err: any) =>
            `${err.loc?.join('.')} - ${err.msg}`
          ).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Update to failed
      setRunningTests(prev => {
        const newMap = new Map(prev);
        newMap.set(testId, {
          action: actionSnapshot,
          source: sourceSnapshot,
          status: 'failed',
          error: errorMessage,
          startTime: Date.now()
        });
        return newMap;
      });

      showNotification('Test Failed', `${actionSnapshot.name}: ${errorMessage}`, 'error');
    }
  };

  const handleQuickTest = async (action: WebCrawlAction, source: IntegrationSource) => {
    if (!integrationId) return;

    const testId = `${source.id}-${action.id}-${Date.now()}`;

    // Add to running tests
    setRunningTests(prev => {
      const newMap = new Map(prev);
      newMap.set(testId, {
        action,
        source,
        status: 'running',
        startTime: Date.now()
      });
      return newMap;
    });

    try {
      // Build test inputs with empty values for non-required params
      const testInputs: Record<string, any> = {};
      action.inputParameters.forEach(param => {
        if (param.required) {
          // For required params, we might want to use a default value
          testInputs[param.name] = param.type === 'number' ? 0 : '';
        }
      });

      const response = await integrationService.testAction(integrationId, {
        sourceId: source.id,
        actionId: action.id,
        testInputs: testInputs
      });

      const resultData = response.data;

      // Update test status to success
      setRunningTests(prev => {
        const newMap = new Map(prev);
        const test = newMap.get(testId);
        if (test) {
          newMap.set(testId, {
            ...test,
            status: 'success',
            result: resultData
          });
        }
        return newMap;
      });

      // Show result modal automatically
      setTestingAction(action);
      setTestResult(resultData);
      setShowTestModal(true);

      showNotification('Test Complete', `${action.name} completed successfully`, 'success');
    } catch (error: any) {
      console.error('Quick test failed:', error);

      let errorMessage = 'Test failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((err: any) =>
            `${err.loc?.join('.')} - ${err.msg}`
          ).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Update test status to failed
      setRunningTests(prev => {
        const newMap = new Map(prev);
        const test = newMap.get(testId);
        if (test) {
          newMap.set(testId, {
            ...test,
            status: 'failed',
            error: errorMessage
          });
        }
        return newMap;
      });

      showNotification('Test Failed', `${action.name}: ${errorMessage}`, 'error');
    }
  };

  const clearCompletedTests = () => {
    setRunningTests(prev => {
      const newMap = new Map(prev);
      Array.from(newMap.entries()).forEach(([key, test]) => {
        if (test.status !== 'running') {
          newMap.delete(key);
        }
      });
      return newMap;
    });
  };

  const closeTestModal = () => {
    setShowTestModal(false);
    setTestingAction(null);
    setTestInputValues({});
    setTestResult(null);
  };

  const renderFieldInput = (field: TranslatedIntegrationField, sourceTypeConfig: TranslatedIntegrationSourceType) => {
    const value = formData[field.id] || '';

    switch (field.type) {
      case 'file':
        return (
          <div>
            <input
              type="file"
              onChange={(e) => handleFileChange(e, field)}
              accept={field.validation?.fileTypes?.join(',')}
              disabled={parsingFile}
              className="block w-full text-sm text-gray-900 border border-gray-200 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-medium file:bg-purple-600 file:text-white hover:file:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {parsingFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-purple-600">
                <Loader className="w-4 h-4 animate-spin" />
                <span>{t('sourceManagement.readingFileColumns', 'Reading file columns…')}</span>
              </div>
            )}
            {field.validation?.maxSize && (
              <p className="mt-1.5 text-xs text-gray-500">Max size: {field.validation.maxSize}MB</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm resize-none"
            required={field.required}
          />
        );

      case 'checkbox': {
        const checked = value === true || value === 'true' || value === '1';
        return (
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {field.label}
            </span>
          </label>
        );
      }

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
            required={field.required}
          >
            <option value="">{t('sourceManagement.selectField', 'Select {{label}}', { label: field.label })}</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        );

      case 'password':
        return (
          <input
            type="password"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
            required={field.required}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.id, parseInt(e.target.value))}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
            required={field.required}
          />
        );

      case 'json':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono text-sm resize-none"
            required={field.required}
          />
        );

      default: // text
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
            required={field.required}
          />
        );
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    return Icon || LucideIcons.Package;
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-gray-50 border-gray-200 text-gray-900',
      green: 'bg-gray-50 border-gray-200 text-gray-900',
      purple: 'bg-gray-50 border-gray-200 text-gray-900',
      orange: 'bg-gray-50 border-gray-200 text-gray-900',
      red: 'bg-gray-50 border-gray-200 text-gray-900'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  if (!integrationConfig) {
    return (
      <AppLayout pageTitle={t('sourceManagement.integrationNotFound', 'Integration Setup')}>
        <div className="h-full flex flex-col bg-main-bg">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('sourceManagement.integrationNotFound', 'Integration Not Found')}</h2>
              <p className="text-gray-600 mb-4">{t('sourceManagement.integrationNotFoundDesc', 'The requested integration does not exist.')}</p>
              <button
                onClick={() => navigate('/settings')}
                className="text-gray-900 hover:text-gray-700 font-medium"
              >
                {t('sourceManagement.backToSettings', 'Back to Settings')}
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const IntegrationIcon = getIcon(integrationConfig.icon);
  const selectedSourceType = integrationConfig.sourceTypes.find(st => st.id === sourceType);

  const innerContent = (
    <>
    <div className={adminMode ? 'w-full' : 'h-full flex flex-col bg-main-bg'}>
      {!adminMode && <MobileHeader title={dmsOnly ? t('settings.integrations.quotes.title', 'Quotes (DMS / ERP)') : `${integrationConfig.name} Setup`} />}

      {/* Sub-header with back button (normal mode only) */}
      {!adminMode && (
        <div className="hidden sm:block bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate('/settings')}
              className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-2 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              {t('settings.backToSettings', 'Back to settings')}
            </button>
          </div>
        </div>
      )}

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 py-6">
          {/* Org-scoped banner */}
          {isOrgScoped && (
            <div className={`mb-4 flex items-center gap-3 rounded-lg px-4 py-3 text-sm border ${isOrgAdmin ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {isOrgAdmin
                ? t('sourceManagement.orgAdminBanner', 'These integrations are shared across your entire organization. Changes you make will affect all members.')
                : t('sourceManagement.orgMemberBanner', 'These integrations are managed by your organization admin. Contact your admin to make changes.')}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">{t('sourceManagement.loading', 'Loading...')}</p>
              </div>
            </div>
          ) : showAddSource ? (
            /* Add/Edit Source Form */
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingSource ? t('sourceManagement.editSource') : t('sourceManagement.addNewSource')}
                  </h2>
                  <button
                    onClick={handleCancelAdd}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t('common.cancel')}
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Source Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sourceManagement.sourceName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    placeholder={t('sourceManagement.sourceNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Admin: Assign to organisations */}
                {adminMode && organizations.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to organisations
                    </label>
                    <select
                      multiple
                      value={localOrgIds.map(String)}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions).map(o => Number(o.value));
                        setLocalOrgIds(selected);
                      }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white h-32"
                    >
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Hold Ctrl / Cmd to select multiple</p>
                    {localOrgIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {localOrgIds.map(id => {
                          const org = organizations.find(o => o.id === id);
                          return org ? (
                            <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                              {org.name}
                              <button type="button" onClick={() => setLocalOrgIds(prev => prev.filter(x => x !== id))} className="hover:text-indigo-900">×</button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Source Type Selection */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('sourceManagement.integrationType')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {integrationConfig.sourceTypes.map(type => {
                      const TypeIcon = getIcon(type.icon);
                      const isSelected = sourceType === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setSourceType(type.id)}
                          disabled={!!editingSource}
                          className={`p-4 border-2 rounded-lg text-left transition-colors ${isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 bg-white'
                            } ${editingSource ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <TypeIcon className={`w-6 h-6 mb-2 ${isSelected ? 'text-purple-600' : 'text-gray-400'
                            }`} />
                          <h3 className={`font-medium mb-1 text-sm ${isSelected ? 'text-purple-700' : 'text-gray-700'
                            }`}>{type.name}</h3>
                          <p className="text-xs text-gray-500">{type.description}</p>
                        </button>
                      );
                    })}
                  </div>
                  {editingSource && (
                    <p className="mt-2 text-xs text-gray-500">{t('sourceManagement.sourceTypeCannotChange')}</p>
                  )}
                </div>

                {/* Dynamic Fields Based on Source Type */}
                {selectedSourceType && (
                  <>
                    {/* Help Content */}
                    <div className={`rounded-lg border p-4 mb-6 ${getColorClasses(selectedSourceType.color)}`}>
                      <h3 className="text-sm font-semibold mb-2">
                        {selectedSourceType.helpContent.title}
                      </h3>
                      <ul className="text-sm space-y-1 ml-5 list-disc">
                        {selectedSourceType.helpContent.items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                      {selectedSourceType.helpContent.examples && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-semibold">{t('sourceManagement.exampleActions')}:</p>
                          {selectedSourceType.helpContent.examples.map((example, idx) => (
                            <div key={idx} className="text-xs bg-white bg-opacity-50 rounded p-2">
                              <strong>{example.title}</strong>: {example.description}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Configuration Fields */}
                    <div className="space-y-4 mb-6">
                      {selectedSourceType.fields.map(field => {
                        // If this is a _column mapping field and we already have a file selected,
                        // show it as a read-only badge (filled by the CSV mapping modal)
                        const isColumnField = field.id.endsWith('_column');
                        const hasFile = !!selectedFile;
                        if (isColumnField && hasFile) {
                          const mappedValue = formData[field.id];
                          return (
                            <div key={field.id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-600">{field.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${mappedValue ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-400'}`}>
                                {mappedValue || (field.required ? 'Not mapped' : 'Not mapped (optional)')}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div key={field.id}>
                            {field.type !== 'checkbox' && (
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                              </label>
                            )}
                            {renderFieldInput(field, selectedSourceType)}
                            {field.description && (
                              <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                            )}
                          </div>
                        );
                      })}
                      {sourceType === 'mcp' && (
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={testMcpConnection}
                            disabled={mcpTest.status === 'running' || !formData.mcpUrl}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {mcpTest.status === 'running' ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            {t('sourceManagement.mcpTest', 'Test connection')}
                          </button>
                          {mcpTest.status === 'ok' && (
                            <span className="inline-flex items-center gap-1 text-sm text-green-700">
                              <CheckCircle className="w-4 h-4" />
                              {t('sourceManagement.mcpTestOk', { count: mcpTest.tools?.length ?? 0, tools: (mcpTest.tools ?? []).map(x => x.name).join(', ') })}
                            </span>
                          )}
                          {mcpTest.status === 'fail' && (
                            <span className="inline-flex items-center gap-1 text-sm text-red-700 break-all">
                              <AlertCircle className="w-4 h-4 shrink-0" />
                              {t('sourceManagement.mcpTestFail', { error: mcpTest.error })}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Re-map columns button */}
                      {selectedFile && selectedSourceType.fields.some(f => f.id.endsWith('_column')) && (
                        <button
                          type="button"
                          onClick={() => {
                            const initialMapping: Record<string, string> = {};
                            selectedSourceType.fields.filter(f => f.id.endsWith('_column')).forEach(f => {
                              initialMapping[f.id] = formData[f.id] || '';
                            });
                            setCsvMappingState(initialMapping);
                            setShowCsvMappingModal(true);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-700 font-medium underline"
                        >
                          {t('sourceManagement.remapColumns', 'Re-map columns')}
                        </button>
                      )}
                    </div>

                    {/* Embedding columns (vector search over the native BQ table) */}
                    {selectedSourceType.fields.some(f => f.type === 'file') && (csvHeaders.length > 0 || embeddingColumns.length > 0) && (
                      <div className="border-t border-gray-200 pt-6 mt-6 mb-6">
                        <div className="mb-3">
                          <h3 className="font-medium text-gray-900">{t('sourceManagement.embeddingColumnsTitle', 'Embedding columns')}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {t('sourceManagement.embeddingColumnsSubtitle', 'Marked columns are concatenated and embedded into an "embeddings" column when the table is created, making the source available for vector (semantic) search. Click to toggle — order matters.')}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Array.from(new Set([...csvHeaders, ...embeddingColumns])).map(col => {
                            const orderIdx = embeddingColumns.indexOf(col);
                            const isSelected = orderIdx >= 0;
                            return (
                              <button
                                key={col}
                                type="button"
                                onClick={() => toggleEmbeddingColumn(col)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${isSelected
                                  ? 'bg-purple-600 border-purple-600 text-white'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'}`}
                              >
                                {isSelected && (
                                  <span className="w-4 h-4 rounded-full bg-white text-purple-600 flex items-center justify-center text-[10px] font-bold">
                                    {orderIdx + 1}
                                  </span>
                                )}
                                {col}
                              </button>
                            );
                          })}
                        </div>
                        {embeddingColumns.length > 0 && (
                          <p className="mt-2 text-xs text-gray-400 font-mono">
                            AI.EMBED(CONCAT({embeddingColumns.join(', ')}))
                          </p>
                        )}
                        {/* Lookup key (ID / SKU) — identifies a matched row; shown
                            with each vector-search match and used as the cart
                            line's part number. */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('sourceManagement.lookupKeyColumn', 'ID / SKU column')}
                          </label>
                          <p className="text-xs text-gray-500 mb-2">
                            {t('sourceManagement.lookupKeyColumnHint', 'The column that identifies a row (part ID / SKU). Shown with each match and used as the part number when added to the cart.')}
                          </p>
                          <select
                            value={formData.lookup_key_column || ''}
                            onChange={e => handleFieldChange('lookup_key_column', e.target.value)}
                            className="w-full sm:w-72 px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
                          >
                            <option value="">{t('sourceManagement.lookupKeyColumnNone', '— none —')}</option>
                            {Array.from(new Set([...csvHeaders, ...(formData.lookup_key_column ? [formData.lookup_key_column] : [])])).map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                        {editingSource && !selectedFile && (
                          <p className="mt-1.5 text-xs text-amber-600">
                            {t('sourceManagement.embeddingColumnsRefreshHint', 'Changes take effect the next time the file is uploaded or the dataset is refreshed.')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* CSV Action Builder (for CSV sources with actionTemplates) */}
                    {(() => {
                      const rawSourceType = integrationConfigRaw?.sourceTypes.find(st => st.id === sourceType);
                      if (!rawSourceType?.actionTemplates?.length) return null;
                      return (
                        <CsvActionBuilder
                          templates={rawSourceType.actionTemplates!}
                          csvHeaders={csvHeaders}
                          actions={crawlActions}
                          onChange={setCrawlActions}
                        />
                      );
                    })()}

                    {/* Action Builder (for webcrawl) */}
                    {selectedSourceType.supportsActions && !integrationConfigRaw?.sourceTypes.find(st => st.id === sourceType)?.actionTemplates?.length && (
                      <div className="border-t border-gray-200 pt-6 mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-medium text-gray-900">{t('actionBuilder.title')}</h3>
                            <p className="text-sm text-gray-600 mt-1">{t('actionBuilder.subtitle')}</p>
                          </div>
                          <button
                            onClick={() => setShowActionForm(!showActionForm)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            {t('actionBuilder.addAction')}
                          </button>
                        </div>

                        {/* Action Form */}
                        {showActionForm && (
                          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                  {t('actionBuilder.actionName')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={actionName}
                                  onChange={(e) => setActionName(e.target.value)}
                                  placeholder={t('actionBuilder.actionNamePlaceholder')}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('actionBuilder.description')}</label>
                                <input
                                  type="text"
                                  value={actionDescription}
                                  onChange={(e) => setActionDescription(e.target.value)}
                                  placeholder={t('actionBuilder.descriptionPlaceholder')}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>

                              {sourceType === 'mcp' && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    {t('actionBuilder.mcpTool', 'Tool')} <span className="text-red-500">*</span>
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={actionTool}
                                      onChange={(e) => setActionTool(e.target.value)}
                                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    >
                                      <option value="">{mcpTest.status === 'running' ? t('actionBuilder.mcpToolLoading', 'Loading tools…') : t('actionBuilder.mcpToolPick', '— pick a tool —')}</option>
                                      {(mcpTest.tools ?? []).map(x => <option key={x.name} value={x.name}>{x.name}</option>)}
                                      {actionTool && !(mcpTest.tools ?? []).some(x => x.name === actionTool) && <option value={actionTool}>{actionTool}</option>}
                                    </select>
                                    <button type="button" onClick={testMcpConnection} disabled={mcpTest.status === 'running'} title={t('sourceManagement.mcpTest', 'Test connection')} className="p-2 text-gray-500 hover:text-purple-700 disabled:opacity-50">
                                      <RefreshCw className={`w-4 h-4 ${mcpTest.status === 'running' ? 'animate-spin' : ''}`} />
                                    </button>
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    {(mcpTest.tools ?? []).find(x => x.name === actionTool)?.description
                                      || (mcpTest.status === 'fail' ? mcpTest.error : t('actionBuilder.mcpToolHint', "The instructions tell the AI how to turn this action's inputs into the tool's arguments."))}
                                  </p>
                                </div>
                              )}

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('actionBuilder.instructions')}</label>
                                <textarea
                                  value={actionInstructions}
                                  onChange={(e) => setActionInstructions(e.target.value)}
                                  placeholder={t('actionBuilder.instructionsPlaceholder')}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                />
                              </div>

                              {/* ── Schema editors ───────────────────────── */}
                              <SchemaTable
                                label={t('schemaEditor.inputSchemaLabel', 'Input Schema')}
                                hint={t('schemaEditor.inputSchemaHint', '(JSON Schema — defines what this action accepts)')}
                                props={inputSchemaProps}
                                onAdd={() => setInputSchemaProps(prev => [...prev, blankProp()])}
                                onUpdate={(idx, field, value) => updateSchemaProp(setInputSchemaProps, idx, field, value)}
                                onDelete={(idx) => deleteSchemaProp(setInputSchemaProps, idx)}
                                buildSchema={buildSchema}
                              />
                              <SchemaTable
                                label={t('schemaEditor.outputSchemaLabel', 'Output Schema')}
                                hint={t('schemaEditor.outputSchemaHint', '(JSON Schema — defines what this action returns)')}
                                props={outputSchemaProps}
                                onAdd={() => setOutputSchemaProps(prev => [...prev, blankProp()])}
                                onUpdate={(idx, field, value) => updateSchemaProp(setOutputSchemaProps, idx, field, value)}
                                onDelete={(idx) => deleteSchemaProp(setOutputSchemaProps, idx)}
                                buildSchema={buildSchema}
                              />

                              <div className="flex justify-end gap-2 pt-2">
                                <button
                                  onClick={resetActionForm}
                                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  {t('common.cancel')}
                                </button>
                                <button
                                  onClick={handleAddAction}
                                  className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                  {editingActionId ? t('actionBuilder.updateAction', 'Update Action') : t('actionBuilder.saveAction', 'Save Action')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Actions List */}
                        {crawlActions.length > 0 && (
                          <div className="space-y-2">
                            {crawlActions.map(action => {
                              const inputProps = action.inputSchema?.properties ? Object.keys(action.inputSchema.properties) : action.inputParameters.map(p => p.name);
                              const outputProps = action.outputSchema?.properties ? Object.keys(action.outputSchema.properties) : [];
                              return (
                                <div key={action.id} className="bg-white border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-gray-900 text-sm mb-1">{action.name}</h4>
                                      {action.description && (
                                        <p className="text-xs text-gray-600 mb-2">{action.description}</p>
                                      )}
                                      <div className="flex flex-wrap gap-2 mt-1.5">
                                        {inputProps.length > 0 && (
                                          <div className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">
                                            <span className="font-semibold">IN:</span>
                                            <span>{inputProps.join(', ')}</span>
                                          </div>
                                        )}
                                        {outputProps.length > 0 && (
                                          <div className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded px-2 py-0.5">
                                            <span className="font-semibold">OUT:</span>
                                            <span>{outputProps.join(', ')}</span>
                                          </div>
                                        )}
                                        {inputProps.length === 0 && outputProps.length === 0 && (
                                          <span className="text-xs text-gray-400">{t('actionBuilder.noSchemaLabel', 'No schema defined')}</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 ml-3">
                                      <button
                                        onClick={() => handleEditAction(action)}
                                        className="p-1 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                        title={t('actionBuilder.editAction', 'Edit action')}
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (!editingSource) {
                                            showNotification(t('common.info'), t('actionBuilder.saveSourceFirst'), 'info');
                                          } else {
                                            handleTestAction(action, editingSource);
                                          }
                                        }}
                                        className="p-1 text-brand-500 hover:bg-brand-50 rounded-lg transition-colors"
                                        title={!editingSource ? t('actionBuilder.saveSourceFirst') : t('actionBuilder.testAction')}
                                      >
                                        <Play className="w-4 h-4 fill-current" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAction(action.id)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title={t('actionBuilder.deleteAction')}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleCancelAdd}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSaveSource}
                    disabled={saving || !sourceName || !sourceType}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        {uploadProgress !== null && uploadProgress < 100
                          ? `${t('sourceManagement.uploading', { defaultValue: 'Uploading' })} ${uploadProgress}%`
                          : t('sourceManagement.saving')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingSource ? t('sourceManagement.updateSource') : t('sourceManagement.saveSource')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Sources List */
            <div className="max-w-7xl mx-auto">
              {integrationId === 'supplier' && !dmsOnly && (
                <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
                  <button
                    onClick={() => setSupplierTab('sources')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${supplierTab === 'sources'
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >
                    {adminMode
                      ? t('sourceManagement.tabSharedSources', 'Shared Integration Sources')
                      : t('sourceManagement.tabSources', 'Data Sources')}
                  </button>
                  <button
                    onClick={() => setSupplierTab('refdocs')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${supplierTab === 'refdocs'
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >
                    {t('sourceManagement.tabReferenceDocs', 'Reference Documents')}
                  </button>
                </div>
              )}
              <div className={integrationId === 'supplier' && supplierTab === 'refdocs' ? 'hidden' : ''}>
              {sources.length > 0 && (!isOrgScoped || isOrgAdmin) && !dmsOnly && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleAddSource}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    {t('sourceManagement.addSource')}
                  </button>
                </div>
              )}
              {sources.length === 0 ? (
                <div className="text-center py-16">
                  <div className="inline-flex p-4 bg-gray-100 rounded-lg mb-4">
                    <IntegrationIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sourceManagement.noSourcesConfigured')}</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    {dmsOnly
                      ? t('sourceManagement.noDmsSources', 'No data source is marked as DMS yet. Open Data Sources and click the DMS tag on a source to show it here.')
                      : t('sourceManagement.getStartedMessage', { name: integrationConfig.name.toLowerCase() })}
                  </p>
                  {(!isOrgScoped || isOrgAdmin) && !dmsOnly && (
                    <button
                      onClick={handleAddSource}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      {t('sourceManagement.addSource')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sources.map(source => {
                    const sourceTypeConfig = integrationConfig.sourceTypes.find(st => st.id === source.type);
                    const SourceTypeIcon = sourceTypeConfig ? getIcon(sourceTypeConfig.icon) : IntegrationIcon;

                    const statusConfig = {
                      active: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-500', text: 'text-green-700', label: t('sourceManagement.status.active', 'Active') },
                      error: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-500', text: 'text-red-700', label: t('sourceManagement.status.error', 'Error') },
                      syncing: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-500', text: 'text-blue-700', label: t('sourceManagement.status.syncing', 'Syncing') },
                      crawling: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-500', text: 'text-purple-700', label: t('sourceManagement.status.crawling', 'Crawling') },
                      inactive: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-400', text: 'text-gray-600', label: t('sourceManagement.status.inactive', 'Inactive') }
                    };

                    const status = statusConfig[source.status];

                    return (
                      <div key={source.id} className="group bg-white rounded-xl border-2 border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
                        {/* Card Header */}
                        <div className="p-5 bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className={`p-3 ${status.icon} rounded-xl shadow-sm flex-shrink-0`}>
                                <SourceTypeIcon className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-bold text-gray-900 text-base truncate mb-0.5">{source.name}</h3>
                                <p className="text-xs text-gray-500 font-medium">{sourceTypeConfig?.name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 transition-opacity duration-200">
                              {/* Shared-source badge (non-admin view) */}
                              {!adminMode && source.shared_organization_ids && source.shared_organization_ids.length > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full text-[10px] font-medium mr-1">
                                  <Shield className="w-3 h-3" />
                                  Shared
                                </span>
                              )}
                              {(adminMode || ((!isOrgScoped || isOrgAdmin) && !source.shared_organization_ids?.length)) ? (
                                <button
                                  onClick={() => handleToggleDms(source)}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border mr-1 transition-colors ${source.config?.isDms
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'bg-white text-gray-400 border-gray-200 hover:text-gray-700 hover:border-gray-400'}`}
                                  title={source.config?.isDms
                                    ? t('sourceManagement.unmarkDms', 'Shown under Quotes (DMS / ERP) — click to unmark')
                                    : t('sourceManagement.markDms', 'Mark as DMS to show it under Quotes (DMS / ERP)')}
                                >
                                  {t('sourceManagement.dmsTag', 'DMS')}
                                </button>
                              ) : source.config?.isDms && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-900 text-white mr-1">{t('sourceManagement.dmsTag', 'DMS')}</span>
                              )}
                              {(!isOrgScoped || isOrgAdmin) && (adminMode || !source.shared_organization_ids?.length) && (
                                <>
                                  <button
                                    onClick={() => handleEditSource(source)}
                                    className="p-2 hover:bg-white rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                                    title={t('sourceManagement.editSourceTooltip', 'Edit source')}
                                  >
                                    <Edit2 className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSource(source.id)}
                                    className="p-2 hover:bg-red-50 rounded-lg transition-all duration-200"
                                    title={t('sourceManagement.deleteSourceTooltip', 'Delete source')}
                                  >
                                    <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Config Details */}
                          {source.config && Object.keys(source.config).length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {sourceTypeConfig?.fields
                                .filter(f => source.config![f.id] !== undefined && source.config![f.id] !== '')
                                .map(f => {
                                  const isPassword = f.type === 'password';
                                  const value = source.config![f.id];
                                  return (
                                    <div key={f.id} className="flex items-center gap-2 text-xs">
                                      <span className="text-gray-400 font-medium shrink-0 min-w-[60px]">{f.label}:</span>
                                      <span className="text-gray-600 truncate font-mono">
                                        {isPassword ? '••••••••' : String(value)}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                          )}

                          {/* Embeddings status badge */}
                          {(() => {
                            const cfg = (source.config || {}) as Record<string, any>;
                            const status = cfg.embedding_status;
                            const hasEmbeds = !!cfg.bqEmbeddings;
                            const errorMsg = cfg.embedding_error;

                            if (status === 'processing') {
                              return (
                                <div className="mt-2.5 flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200/80 rounded-lg text-xs font-medium w-fit animate-pulse">
                                  <Loader className="w-3.5 h-3.5 animate-spin text-amber-600 shrink-0" />
                                  <span>Embeddings: Processing...</span>
                                </div>
                              );
                            }
                            if (hasEmbeds || status === 'ready') {
                              return (
                                <div className="mt-2.5 flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-800 border border-purple-200/80 rounded-lg text-xs font-medium w-fit">
                                  <Sparkles className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                                  <span>Embeddings: Active</span>
                                </div>
                              );
                            }
                            if (status === 'failed') {
                              return (
                                <div className="mt-2.5 flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-800 border border-red-200/80 rounded-lg text-xs font-medium w-fit" title={errorMsg || ''}>
                                  <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                  <span>Embeddings: Failed</span>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Admin: technical identifiers — the source id and the
                              BigQuery table(s) backing it, so an admin can trace a
                              source to its dataset. Click a value to copy it. */}
                          {adminMode && (() => {
                            const cfg = (source.config || {}) as Record<string, any>;
                            const bqTable = cfg.bqTable || null;
                            const bqExternal = cfg.bqExternalTable && cfg.bqExternalTable !== cfg.bqTable ? cfg.bqExternalTable : null;
                            const rows: Array<{ label: string; value: string; badge?: boolean }> = [
                              { label: t('sourceManagement.sourceId', 'Source ID'), value: String(source.id) },
                            ];
                            if (bqTable) rows.push({ label: t('sourceManagement.bqTable', 'BigQuery table'), value: String(bqTable), badge: !!cfg.bqEmbeddings });
                            if (bqExternal) rows.push({ label: t('sourceManagement.bqExternalTable', 'BQ external table'), value: String(bqExternal) });
                            return (
                              <div className="mt-2 pt-2 border-t border-dashed border-gray-100 space-y-1.5">
                                {rows.map(r => (
                                  <div key={r.label} className="flex items-start gap-2 text-xs">
                                    <span className="text-gray-400 font-medium shrink-0 min-w-[60px] pt-0.5">{r.label}:</span>
                                    <button
                                      type="button"
                                      onClick={() => navigator.clipboard?.writeText(r.value)}
                                      title={t('sourceManagement.clickToCopy', 'Click to copy') + `\n${r.value}`}
                                      className="text-gray-600 font-mono break-all hover:text-indigo-600 transition-colors text-left min-w-0 flex-1"
                                    >
                                      {r.value}
                                    </button>
                                    {r.badge && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[9px] font-semibold shrink-0">
                                        {t('sourceManagement.embeddings', 'embeddings')}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Admin: Assigned orgs */}
                          {adminMode && (source as any).shared_organization_ids?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {((source as any).shared_organization_ids as number[]).map(id => {
                                const org = organizations.find(o => o.id === id);
                                return org ? (
                                  <span key={id} className="inline-flex items-center px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-medium">
                                    {org.name}
                                  </span>
                                ) : (
                                  <span key={id} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px]">
                                    Org #{id}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {adminMode && !((source as any).shared_organization_ids?.length) && (
                            <div className="mt-2">
                              <span className="text-[10px] text-orange-500 font-medium">⚠ Not assigned to any org</span>
                            </div>
                          )}

                        </div>

                        {/* Card Body - Actions */}
                        {source.actions && source.actions.length > 0 && (
                          <div className="p-5">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                                <div className="w-1 h-4 bg-gradient-to-b from-brand-500 to-brand-600 rounded-full"></div>
                                Actions
                                <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-brand-500 bg-brand-100 rounded-full">
                                  {source.actions.length}
                                </span>
                              </h4>
                            </div>
                            <div className="space-y-2">
                              {source.actions.slice(0, 3).map(action => (
                                <div
                                  key={action.id}
                                  className="group/action bg-gradient-to-r from-gray-50 to-white hover:from-brand-50 hover:to-brand-50/50 border border-gray-200 hover:border-brand-200 rounded-lg p-3 transition-all duration-200 hover:shadow-md"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1.5 h-1.5 bg-brand-500 rounded-full group-hover/action:animate-pulse"></div>
                                        <span className="font-semibold text-sm text-gray-900 truncate">{action.name}</span>
                                      </div>
                                      {action.description && (
                                        <p className="text-xs text-gray-600 line-clamp-1 ml-3.5">{action.description}</p>
                                      )}
                                      {action.inputParameters.length > 0 && (
                                        <div className="flex items-center gap-1 mt-1.5 ml-3.5">
                                          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Params:</span>
                                          <div className="flex items-center gap-1 flex-wrap">
                                            {action.inputParameters.slice(0, 2).map((param, idx) => (
                                              <span key={idx} className="inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-700">
                                                {param.name}
                                              </span>
                                            ))}
                                            {action.inputParameters.length > 2 && (
                                              <span className="text-[10px] text-gray-500">
                                                +{action.inputParameters.length - 2}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-shrink-0">
                                      {action.inputParameters.length > 0 ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTestAction(action, source);
                                          }}
                                          className="p-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md group-hover/action:scale-110"
                                          title={t('actionBuilder.testWithInputs')}
                                        >
                                          <Play className="w-3.5 h-3.5 fill-current" />
                                        </button>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleQuickTest(action, source);
                                          }}
                                          className="p-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md group-hover/action:scale-110"
                                          title={t('actionBuilder.quickTest')}
                                        >
                                          <Play className="w-3.5 h-3.5 fill-current" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {source.actions.length > 3 && (!isOrgScoped || isOrgAdmin) && (adminMode || !source.shared_organization_ids?.length) && (
                                <button
                                  onClick={() => handleEditSource(source)}
                                  className="w-full text-xs text-gray-600 hover:text-gray-900 font-semibold py-2 px-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-gray-200 hover:border-gray-300"
                                >
                                  {t('actionBuilder.viewMoreActions', { count: source.actions.length - 3 })}
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Empty State for No Actions */}
                        {(!source.actions || source.actions.length === 0) && (
                          <div className="p-5 text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-2">
                              <Play className="w-5 h-5 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-500 font-medium">{t('actionBuilder.noActionsConfigured')}</p>
                            {(!isOrgScoped || isOrgAdmin) && (adminMode || !source.shared_organization_ids?.length) && (
                              <button
                                onClick={() => handleEditSource(source)}
                                className="mt-2 text-xs text-brand-500 hover:text-brand-600 font-semibold"
                              >
                                {t('actionBuilder.addActions')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              </div>

              {/* Reference documents sub-tab — org mode on the Settings page,
                  shared/admin mode inside the Admin panel's Shared
                  Integrations tab. */}
              {integrationId === 'supplier' && supplierTab === 'refdocs' && (
                <EnrichmentSourcesSection
                  mode={adminMode ? 'admin' : 'org'}
                  organizations={organizations}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Test Action Modal - Input Parameters */}
      {showTestModal && testingAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl animate-in slide-in-from-bottom-4 duration-300">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-100">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Play className="w-5 h-5 text-brand-500 fill-current" />
                    <h3 className="text-lg font-semibold text-gray-900">{testingAction.name}</h3>
                  </div>
                  {testingAction.description && (
                    <p className="text-sm text-gray-600 ml-7">{testingAction.description}</p>
                  )}
                </div>
                <button
                  onClick={closeTestModal}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Test Inputs */}
              {testingAction.inputParameters.length > 0 ? (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{t('testModal.inputParameters', 'Input Parameters')}</h4>
                  <div className="space-y-3">
                    {testingAction.inputParameters.map((param, idx) => (
                      <div key={idx}>
                        <label className="block text-sm text-gray-700 mb-1.5 font-medium">
                          {param.name}
                          {param.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {param.description && (
                          <p className="text-xs text-gray-500 mb-2">{param.description}</p>
                        )}
                        {param.type === 'number' ? (
                          <input
                            type="number"
                            value={testInputValues[param.name] || ''}
                            onChange={(e) => setTestInputValues(prev => ({ ...prev, [param.name]: parseFloat(e.target.value) || 0 }))}
                            placeholder={t('testModal.enterParam', 'Enter {{name}}', { name: param.name })}
                    className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all outline-none"
                          />
                        ) : (
                          <input
                            type="text"
                            value={testInputValues[param.name] || ''}
                            onChange={(e) => setTestInputValues(prev => ({ ...prev, [param.name]: e.target.value }))}
                            placeholder={t('testModal.enterParam', 'Enter {{name}}', { name: param.name })}
                            className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all outline-none"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-6 text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">{t('testModal.noInputParameters', 'This action has no input parameters')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('testModal.noInputParametersHint', 'Click "Run Test" to start the test')}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={closeTestModal}
                  className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium border border-gray-200"
                >
                  {t('testModal.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleRunTest}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium shadow-sm hover:shadow"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>{t('testModal.runTest', 'Run Test')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Running Tests Tracker - Bottom of Screen */}
      {runningTests.size > 0 && (
        <div className="fixed bottom-6 right-6 z-40 w-80 max-w-[calc(100vw-3rem)] animate-in slide-in-from-bottom-2 fade-in duration-300">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100/50">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-brand-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {t('testModal.running', '{{count}} running', { count: Array.from(runningTests.values()).filter(t => t.status === 'running').length })}
                </span>
              </div>
              <button
                onClick={clearCompletedTests}
                className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
              >
                {t('testModal.clear', 'Clear')}
              </button>
            </div>

            {/* Tests List */}
            <div className="max-h-96 overflow-y-auto">
              <div className="divide-y divide-gray-100/50">
                {Array.from(runningTests.entries()).map(([testId, test]) => (
                  <div
                    key={testId}
                    className="px-4 py-3 hover:bg-gray-50/50 transition-all duration-200 animate-in fade-in slide-in-from-top-1"
                  >
                    <div className="flex items-start gap-3">
                      {/* Status Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {test.status === 'running' && (
                          <div className="relative">
                            <Loader className="w-4 h-4 text-brand-500 animate-spin" strokeWidth={2.5} />
                          </div>
                        )}
                        {test.status === 'success' && (
                          <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-green-600" strokeWidth={2.5} />
                          </div>
                        )}
                        {test.status === 'failed' && (
                          <div className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertCircle className="w-3 h-3 text-red-600" strokeWidth={2.5} />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {test.action.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {test.source.name}
                            </p>
                            {test.status === 'running' && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <div className="flex gap-0.5">
                                  <div className="w-1 h-1 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                  <div className="w-1 h-1 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                  <div className="w-1 h-1 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                                <span className="text-xs text-gray-400">{t('testModal.processing', 'Processing')}</span>
                              </div>
                            )}
                            {test.error && (
                              <p className="text-xs text-red-500 mt-1.5 line-clamp-2">{test.error}</p>
                            )}
                          </div>

                          {/* Action Button */}
                          {test.result && (
                            <button
                              onClick={() => {
                                setTestingAction(test.action);
                                setSelectedSource(test.source);
                                setTestResult(test.result);
                                setShowTestModal(true);
                              }}
                              className="flex-shrink-0 text-xs text-gray-600 hover:text-gray-900 font-medium px-2.5 py-1 hover:bg-gray-100 rounded-lg transition-all duration-200"
                            >
                              {t('testModal.view', 'View')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Results Modal */}
      {showTestModal && testResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-xl animate-in slide-in-from-bottom-4 duration-300">
            <div className="overflow-y-auto max-h-[85vh]">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {testingAction?.name} - {t('testModal.testResults', 'Test Results')}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={closeTestModal}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Test Results */}
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2">
                      {testResult.success ? (
                        <>
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium text-green-700">{t('testModal.success', 'Success')}</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span className="text-sm font-medium text-red-700">{t('testModal.failed', 'Failed')}</span>
                        </>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {testResult.executionTime}s
                    </span>
                  </div>

                  {/* Error Message */}
                  {testResult.error && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-sm text-red-700">{testResult.error}</p>
                    </div>
                  )}

                  {/* Output */}
                  {testResult.output && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('testModal.output', 'Output')}</label>
                      <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-auto">
                        <pre className="text-xs text-gray-100 whitespace-pre-wrap font-mono leading-relaxed">
                          {typeof testResult.output === 'string'
                            ? testResult.output
                            : JSON.stringify(testResult.output, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={closeTestModal}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium"
                  >
                    {t('testModal.close', 'Close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      />

      {/* CSV Column Mapping Modal */}
      {showCsvMappingModal && (() => {
        const selectedSourceTypeLocal = integrationConfig?.sourceTypes.find(st => st.id === sourceType);
        const columnFields = selectedSourceTypeLocal?.fields.filter(f => f.id.endsWith('_column')) ?? [];
        return (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('csvMappingModal.title', 'Map CSV Columns')}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t('csvMappingModal.columnsDetected', '{{name}} — {{count}} columns detected', { name: pendingCsvFile?.name, count: csvHeaders.length })}
                  </p>
                </div>
                <button
                  onClick={() => { setShowCsvMappingModal(false); setPendingCsvFile(null); }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mapping rows */}
              <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
                {columnFields.map(field => (
                  <div key={field.id} className="flex items-center gap-3">
                    <div className="w-48 flex-shrink-0">
                      <span className="text-sm font-medium text-gray-700">{field.label}</span>
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                      {field.description && (
                        <p className="text-xs text-gray-400 mt-0.5 leading-tight">{field.description}</p>
                      )}
                    </div>
                    <select
                      value={csvMappingState[field.id] || ''}
                      onChange={e => setCsvMappingState(prev => ({ ...prev, [field.id]: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white"
                    >
                      <option value="">— {field.required ? t('csvMappingModal.selectColumn', 'Select column') : t('csvMappingModal.optional', 'Optional')} —</option>
                      {csvHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => { setShowCsvMappingModal(false); setPendingCsvFile(null); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  {t('csvMappingModal.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleCsvMappingConfirm}
                  disabled={columnFields.filter(f => f.required).some(f => !csvMappingState[f.id])}
                  className="px-5 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg font-medium transition-colors"
                >
                  {t('csvMappingModal.confirmMapping', 'Confirm Mapping')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );

  if (adminMode) {
    return innerContent;
  }

  return (
    <AppLayout pageTitle={dmsOnly ? t('settings.integrations.quotes.title', 'Quotes (DMS / ERP)') : `${integrationConfig.name} Setup`}>
      {innerContent}
    </AppLayout>
  );
};

export default GenericIntegrationSetupPage;
